import { graphql } from 'gatsby';
import { type ImageItem } from '@kontent-ai/gatsby-components/dist/image-element';
import { lazy, Suspense } from 'react';
import { RichTextElement } from '@kontent-ai/gatsby-components';
import 'highlight.js/styles/github-dark.css';
import { isRichTextValid, resolveDOMNode } from './utils';
import useResolveRichTextInternalLink from './hooks/useResolveRichTextInternalLink';
import { StyledSection, StyledContainer, StyledRichText } from './RichTextBlock.styles';
import Image from './components/Image/Image';

const CodeSnippet = lazy(() => import('./components/CodeSnippet/CodeSnippet'));
const Quote = lazy(() => import('./components/Quote/Quote'));
const Table = lazy(() => import('./components/Table/Table'));
const Video = lazy(() => import('@/components/Video/Video'));

type ResolveLinkedItemsProps =
  | Queries.CODE_SNIPPETFragment
  | Queries.QUOTEFragment
  | Queries.TABLEFragment
  | Queries.VIDEOFragment;

const resolveLinkedItems = (item: ResolveLinkedItemsProps) => {
  if (!item) {
    return null;
  }

  let component: JSX.Element;

  switch (item.__typename) {
    case 'kontent_item_code_snippet':
      component = <CodeSnippet {...item} />;
      break;
    case 'kontent_item_quote':
      component = <Quote {...item} />;
      break;
    case 'kontent_item_table':
      component = <Table {...item} />;
      break;
    case 'kontent_item_video':
      component = <Video {...item} />;
      break;
    default:
      return null;
  }

  return <Suspense>{component}</Suspense>;
};

const RichTextBlock = ({
  elements,
  system,
}: {
  elements: Omit<
    Queries.RICH_TEXT_BLOCKFragment['richTextBlockElements'],
    'component_a_b_test_snippet__a_b_test_campaign'
  >;
  system?: Queries.RICH_TEXT_BLOCKFragment['system'];
}) => {
  const { value, links, images, modular_content } = elements.rich_text_content;
  const resolveRichTextInternalLink = useResolveRichTextInternalLink();

  if (!isRichTextValid(value)) {
    return null;
  }

  return (
    <StyledSection data-codename={system?.codename ?? 'rich-text'}>
      <StyledContainer>
        <StyledRichText>
          <RichTextElement
            linkedItems={modular_content as any[]}
            images={images as ImageItem[]}
            links={links}
            resolveImage={Image}
            resolveLink={resolveRichTextInternalLink}
            resolveLinkedItem={resolveLinkedItems}
            resolveDomNode={resolveDOMNode}
            value={value}
          />
        </StyledRichText>
      </StyledContainer>
    </StyledSection>
  );
};

export default RichTextBlock;

export const fragments = graphql`
  fragment RICH_TEXT_BLOCK on kontent_item_rich_text_block {
    __typename
    system {
      codename
      type
      id
    }
    richTextBlockElements: elements {
      ...COMPONENT_A_B_TEST
      rich_text_content {
        value
        images {
          description
          height
          url
          image_id
          width
        }
        links {
          link_id
          codename
        }
        modular_content {
          ...CODE_SNIPPET
          ...TABLE
          ...VIDEO
          ...QUOTE
        }
      }
    }
  }
`;
